/**
 * Contractor Database Component
 * Enhanced version with more fields, advanced filtering, and export
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
  Users, Plus, Search, Star, Phone, Mail, Shield, Award,
  Building2, MapPin, Trash2, Edit, Save, X, FileText, Filter,
  Globe, Clock, DollarSign, Briefcase, Download, ChevronDown,
  ChevronUp, AlertCircle, CheckCircle, Calendar, Tag
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { ScrollArea } from './ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from './ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';

const AVAILABILITY_COLORS = {
  available: 'bg-green-500',
  busy: 'bg-yellow-500',
  unavailable: 'bg-red-500',
  on_project: 'bg-blue-500',
  vacation: 'bg-purple-500'
};

const ContractorDatabase = () => {
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [specialtyFilter, setSpecialtyFilter] = useState('');
  const [availabilityFilter, setAvailabilityFilter] = useState('');
  const [ratingFilter, setRatingFilter] = useState('');
  const [options, setOptions] = useState({ 
    specialties: [], 
    certifications: [],
    payment_terms: [],
    availability_statuses: [],
    contact_methods: []
  });
  const [stats, setStats] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  
  // Dialog state
  const [showDialog, setShowDialog] = useState(false);
  const [editingContractor, setEditingContractor] = useState(null);
  const [activeTab, setActiveTab] = useState('basic');
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    phone: '',
    email: '',
    license_number: '',
    insurance_provider: '',
    insurance_expiry: '',
    insurance_policy_number: '',
    insurance_coverage_amount: '',
    certifications: [],
    specialty: '',
    secondary_specialties: [],
    rating: null,
    notes: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    website: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    payment_terms: '',
    hourly_rate: '',
    day_rate: '',
    tax_id: '',
    bonding_company: '',
    bonding_limit: '',
    years_in_business: '',
    num_employees: '',
    union_affiliated: false,
    preferred_contact_method: '',
    availability_status: 'available',
    tags: []
  });
  const [newTag, setNewTag] = useState('');

  // Load options
  useEffect(() => {
    const loadOptions = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contractors/options`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setOptions(data);
        }
      } catch (error) {
        console.error('Failed to load options:', error);
      }
    };
    loadOptions();
  }, []);

  // Load contractors
  const loadContractors = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${process.env.REACT_APP_BACKEND_URL}/api/contractors/?limit=100`;
      if (searchTerm) url += `&search=${encodeURIComponent(searchTerm)}`;
      if (specialtyFilter) url += `&specialty=${encodeURIComponent(specialtyFilter)}`;
      if (availabilityFilter) url += `&availability=${encodeURIComponent(availabilityFilter)}`;
      if (ratingFilter) url += `&rating_min=${ratingFilter}`;
      
      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setContractors(data.contractors);
      }
    } catch (error) {
      console.error('Failed to load contractors:', error);
      toast.error('Failed to load contractors');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, specialtyFilter, availabilityFilter, ratingFilter]);

  // Load stats
  const loadStats = useCallback(async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contractors/stats/summary`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  }, []);

  useEffect(() => {
    loadContractors();
    loadStats();
  }, [loadContractors, loadStats]);

  // Handle form change
  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Handle certification toggle
  const toggleCertification = (cert) => {
    setFormData(prev => ({
      ...prev,
      certifications: prev.certifications.includes(cert)
        ? prev.certifications.filter(c => c !== cert)
        : [...prev.certifications, cert]
    }));
  };

  // Handle secondary specialty toggle
  const toggleSecondarySpecialty = (spec) => {
    setFormData(prev => ({
      ...prev,
      secondary_specialties: prev.secondary_specialties.includes(spec)
        ? prev.secondary_specialties.filter(s => s !== spec)
        : [...prev.secondary_specialties, spec]
    }));
  };

  // Add tag
  const addTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  // Remove tag
  const removeTag = (tag) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }));
  };

  // Open dialog for new contractor
  const handleAddNew = () => {
    setEditingContractor(null);
    setActiveTab('basic');
    setFormData({
      name: '',
      company: '',
      phone: '',
      email: '',
      license_number: '',
      insurance_provider: '',
      insurance_expiry: '',
      insurance_policy_number: '',
      insurance_coverage_amount: '',
      certifications: [],
      specialty: '',
      secondary_specialties: [],
      rating: null,
      notes: '',
      address: '',
      city: '',
      state: '',
      zip_code: '',
      website: '',
      emergency_contact_name: '',
      emergency_contact_phone: '',
      payment_terms: '',
      hourly_rate: '',
      day_rate: '',
      tax_id: '',
      bonding_company: '',
      bonding_limit: '',
      years_in_business: '',
      num_employees: '',
      union_affiliated: false,
      preferred_contact_method: '',
      availability_status: 'available',
      tags: []
    });
    setShowDialog(true);
  };

  // Open dialog for editing
  const handleEdit = (contractor) => {
    setEditingContractor(contractor);
    setActiveTab('basic');
    setFormData({
      name: contractor.name || '',
      company: contractor.company || '',
      phone: contractor.phone || '',
      email: contractor.email || '',
      license_number: contractor.license_number || '',
      insurance_provider: contractor.insurance_provider || '',
      insurance_expiry: contractor.insurance_expiry || '',
      insurance_policy_number: contractor.insurance_policy_number || '',
      insurance_coverage_amount: contractor.insurance_coverage_amount || '',
      certifications: contractor.certifications || [],
      specialty: contractor.specialty || '',
      secondary_specialties: contractor.secondary_specialties || [],
      rating: contractor.rating || null,
      notes: contractor.notes || '',
      address: contractor.address || '',
      city: contractor.city || '',
      state: contractor.state || '',
      zip_code: contractor.zip_code || '',
      website: contractor.website || '',
      emergency_contact_name: contractor.emergency_contact_name || '',
      emergency_contact_phone: contractor.emergency_contact_phone || '',
      payment_terms: contractor.payment_terms || '',
      hourly_rate: contractor.hourly_rate || '',
      day_rate: contractor.day_rate || '',
      tax_id: contractor.tax_id || '',
      bonding_company: contractor.bonding_company || '',
      bonding_limit: contractor.bonding_limit || '',
      years_in_business: contractor.years_in_business || '',
      num_employees: contractor.num_employees || '',
      union_affiliated: contractor.union_affiliated || false,
      preferred_contact_method: contractor.preferred_contact_method || '',
      availability_status: contractor.availability_status || 'available',
      tags: contractor.tags || []
    });
    setShowDialog(true);
  };

  // Save contractor
  const handleSave = async () => {
    if (!formData.name || !formData.company) {
      toast.error('Name and Company are required');
      return;
    }

    // Prepare data - convert numeric strings to numbers
    const dataToSend = {
      ...formData,
      insurance_coverage_amount: formData.insurance_coverage_amount ? parseFloat(formData.insurance_coverage_amount) : null,
      hourly_rate: formData.hourly_rate ? parseFloat(formData.hourly_rate) : null,
      day_rate: formData.day_rate ? parseFloat(formData.day_rate) : null,
      bonding_limit: formData.bonding_limit ? parseFloat(formData.bonding_limit) : null,
      years_in_business: formData.years_in_business ? parseInt(formData.years_in_business) : null,
      num_employees: formData.num_employees ? parseInt(formData.num_employees) : null
    };

    try {
      const url = editingContractor
        ? `${process.env.REACT_APP_BACKEND_URL}/api/contractors/${editingContractor.contractor_id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/contractors/`;
      
      const method = editingContractor ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(dataToSend)
      });

      if (response.ok) {
        toast.success(editingContractor ? 'Contractor updated' : 'Contractor added');
        setShowDialog(false);
        loadContractors();
        loadStats();
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save contractor');
    }
  };

  // Delete contractor
  const handleDelete = async (contractorId) => {
    if (!window.confirm('Are you sure you want to delete this contractor?')) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/contractors/${contractorId}`,
        { method: 'DELETE', credentials: 'include' }
      );

      if (response.ok) {
        toast.success('Contractor deleted');
        loadContractors();
        loadStats();
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete contractor');
    }
  };

  // Export contractors
  const handleExport = async (format) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/contractors/export?format=${format}`,
        { credentials: 'include' }
      );
      
      if (response.ok) {
        const data = await response.json();
        
        if (format === 'csv') {
          const blob = new Blob([data.csv], { type: 'text/csv' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `contractors_${new Date().toISOString().split('T')[0]}.csv`);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          toast.success(`Exported ${data.count} contractors to CSV`);
        } else {
          const blob = new Blob([JSON.stringify(data.contractors, null, 2)], { type: 'application/json' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `contractors_${new Date().toISOString().split('T')[0]}.json`);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          toast.success(`Exported ${data.count} contractors to JSON`);
        }
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export contractors');
    }
  };

  // Render stars
  const renderStars = (rating, interactive = false, onChange = null) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map(i => (
          <Star
            key={i}
            onClick={interactive ? () => onChange(i) : undefined}
            className={`w-4 h-4 ${interactive ? 'cursor-pointer' : ''} ${i <= (rating || 0) ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground'} ${interactive ? 'hover:text-yellow-400' : ''}`}
          />
        ))}
      </div>
    );
  };

  // Format currency
  const formatCurrency = (num) => {
    if (!num) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(num);
  };

  return (
    <div className="space-y-6 p-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Users className="w-6 h-6 text-primary" />
            Contractor Database
          </h2>
          <p className="text-muted-foreground text-sm">Manage your contractor contacts and information</p>
        </div>
        <div className="flex gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => handleExport('csv')}>
                Export as CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('json')}>
                Export as JSON
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button onClick={handleAddNew} data-testid="add-contractor-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Contractor
          </Button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="text-3xl font-bold text-primary">{stats.total}</div>
              <div className="text-sm text-muted-foreground">Total</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-3xl font-bold text-green-500">{stats.available}</div>
              <div className="text-sm text-muted-foreground">Available</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-3xl font-bold text-yellow-500">{stats.avg_rating || '-'}</div>
              <div className="text-sm text-muted-foreground">Avg Rating</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-3xl font-bold text-blue-500">{stats.with_insurance}</div>
              <div className="text-sm text-muted-foreground">Insured</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-3xl font-bold text-purple-500">{stats.with_license}</div>
              <div className="text-sm text-muted-foreground">Licensed</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-3xl font-bold text-orange-500">{stats.total_projects}</div>
              <div className="text-sm text-muted-foreground">Projects</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search & Filter */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name, company, or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                  data-testid="contractor-search"
                />
              </div>
              <Button 
                variant="outline" 
                onClick={() => setShowFilters(!showFilters)}
                className="w-full sm:w-auto"
              >
                <Filter className="w-4 h-4 mr-2" />
                Filters
                {showFilters ? <ChevronUp className="w-4 h-4 ml-2" /> : <ChevronDown className="w-4 h-4 ml-2" />}
              </Button>
            </div>
            
            {showFilters && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2 border-t">
                <Select value={specialtyFilter || "all"} onValueChange={(v) => setSpecialtyFilter(v === "all" ? "" : v)}>
                  <SelectTrigger data-testid="specialty-filter">
                    <SelectValue placeholder="All Specialties" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Specialties</SelectItem>
                    {options.specialties.map(spec => (
                      <SelectItem key={spec} value={spec}>{spec}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={availabilityFilter || "all"} onValueChange={(v) => setAvailabilityFilter(v === "all" ? "" : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Availability" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Availability</SelectItem>
                    {options.availability_statuses?.map(status => (
                      <SelectItem key={status} value={status}>
                        {status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={ratingFilter || "all"} onValueChange={(v) => setRatingFilter(v === "all" ? "" : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Min Rating" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any Rating</SelectItem>
                    <SelectItem value="5">5 Stars</SelectItem>
                    <SelectItem value="4">4+ Stars</SelectItem>
                    <SelectItem value="3">3+ Stars</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Contractor List */}
      <div className="space-y-4">
        {loading ? (
          <div className="text-center py-8 text-muted-foreground">Loading contractors...</div>
        ) : contractors.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-semibold mb-2">No contractors found</h3>
              <p className="text-muted-foreground mb-4">Add your first contractor to get started</p>
              <Button onClick={handleAddNew}>
                <Plus className="w-4 h-4 mr-2" />
                Add Contractor
              </Button>
            </CardContent>
          </Card>
        ) : (
          contractors.map(contractor => (
            <Card key={contractor.contractor_id} className="hover:shadow-md transition-shadow">
              <CardContent className="pt-4">
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold">{contractor.name}</h3>
                      {contractor.rating && renderStars(contractor.rating)}
                      {contractor.availability_status && (
                        <Badge 
                          variant="outline" 
                          className={`${AVAILABILITY_COLORS[contractor.availability_status]} bg-opacity-20`}
                        >
                          <span className={`w-2 h-2 rounded-full ${AVAILABILITY_COLORS[contractor.availability_status]} mr-1`}></span>
                          {contractor.availability_status.replace(/_/g, ' ')}
                        </Badge>
                      )}
                    </div>
                    <p className="text-muted-foreground flex items-center gap-2 mb-2">
                      <Building2 className="w-4 h-4" />
                      {contractor.company}
                      {contractor.years_in_business && (
                        <span className="text-xs">• {contractor.years_in_business} years</span>
                      )}
                    </p>
                    <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                      {contractor.phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="w-3 h-3" /> {contractor.phone}
                        </span>
                      )}
                      {contractor.email && (
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" /> {contractor.email}
                        </span>
                      )}
                      {contractor.website && (
                        <a href={contractor.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-primary hover:underline">
                          <Globe className="w-3 h-3" /> Website
                        </a>
                      )}
                      {contractor.hourly_rate && (
                        <span className="flex items-center gap-1">
                          <DollarSign className="w-3 h-3" /> {formatCurrency(contractor.hourly_rate)}/hr
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {contractor.specialty && (
                        <Badge variant="secondary">{contractor.specialty}</Badge>
                      )}
                      {contractor.secondary_specialties?.slice(0, 2).map(spec => (
                        <Badge key={spec} variant="outline">{spec}</Badge>
                      ))}
                      {contractor.secondary_specialties?.length > 2 && (
                        <Badge variant="outline">+{contractor.secondary_specialties.length - 2}</Badge>
                      )}
                    </div>
                    {contractor.certifications?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {contractor.certifications.slice(0, 3).map(cert => (
                          <Badge key={cert} variant="outline" className="text-xs">
                            <Award className="w-3 h-3 mr-1" />
                            {cert}
                          </Badge>
                        ))}
                        {contractor.certifications.length > 3 && (
                          <Badge variant="outline" className="text-xs">
                            +{contractor.certifications.length - 3} more
                          </Badge>
                        )}
                      </div>
                    )}
                    {contractor.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {contractor.tags.map(tag => (
                          <Badge key={tag} variant="secondary" className="text-xs">
                            <Tag className="w-3 h-3 mr-1" />
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleEdit(contractor)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="text-destructive"
                      onClick={() => handleDelete(contractor.contractor_id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>
              {editingContractor ? 'Edit Contractor' : 'Add New Contractor'}
            </DialogTitle>
          </DialogHeader>
          
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 overflow-hidden flex flex-col">
            <TabsList className="w-full justify-start">
              <TabsTrigger value="basic">Basic Info</TabsTrigger>
              <TabsTrigger value="business">Business Details</TabsTrigger>
              <TabsTrigger value="insurance">Insurance & Bonding</TabsTrigger>
              <TabsTrigger value="certs">Certifications</TabsTrigger>
            </TabsList>
            
            <ScrollArea className="flex-1 pr-4">
              {/* Basic Info Tab */}
              <TabsContent value="basic" className="space-y-4 mt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Name *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => handleFormChange('name', e.target.value)}
                      placeholder="Contact name"
                      data-testid="contractor-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Company *</Label>
                    <Input
                      value={formData.company}
                      onChange={(e) => handleFormChange('company', e.target.value)}
                      placeholder="Company name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => handleFormChange('phone', e.target.value)}
                      placeholder="(555) 555-5555"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleFormChange('email', e.target.value)}
                      placeholder="email@example.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Website</Label>
                    <Input
                      value={formData.website}
                      onChange={(e) => handleFormChange('website', e.target.value)}
                      placeholder="https://example.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Preferred Contact Method</Label>
                    <Select value={formData.preferred_contact_method} onValueChange={(v) => handleFormChange('preferred_contact_method', v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select method" />
                      </SelectTrigger>
                      <SelectContent>
                        {options.contact_methods?.map(method => (
                          <SelectItem key={method} value={method}>{method}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <Separator />
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Address</Label>
                    <Input
                      value={formData.address}
                      onChange={(e) => handleFormChange('address', e.target.value)}
                      placeholder="Street address"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>City</Label>
                    <Input
                      value={formData.city}
                      onChange={(e) => handleFormChange('city', e.target.value)}
                      placeholder="City"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>State</Label>
                    <Input
                      value={formData.state}
                      onChange={(e) => handleFormChange('state', e.target.value)}
                      placeholder="State"
                      maxLength={2}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>ZIP Code</Label>
                    <Input
                      value={formData.zip_code}
                      onChange={(e) => handleFormChange('zip_code', e.target.value)}
                      placeholder="ZIP"
                    />
                  </div>
                </div>
                
                <Separator />
                
                <div className="space-y-2">
                  <Label>Emergency Contact</Label>
                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      value={formData.emergency_contact_name}
                      onChange={(e) => handleFormChange('emergency_contact_name', e.target.value)}
                      placeholder="Name"
                    />
                    <Input
                      value={formData.emergency_contact_phone}
                      onChange={(e) => handleFormChange('emergency_contact_phone', e.target.value)}
                      placeholder="Phone"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Rating</Label>
                  <div className="flex gap-2">
                    {[1, 2, 3, 4, 5].map(i => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => handleFormChange('rating', i)}
                        className="focus:outline-none"
                      >
                        <Star
                          className={`w-8 h-8 cursor-pointer transition-colors ${
                            i <= (formData.rating || 0) ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground hover:text-yellow-400'
                          }`}
                        />
                      </button>
                    ))}
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Availability Status</Label>
                  <Select value={formData.availability_status} onValueChange={(v) => handleFormChange('availability_status', v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      {options.availability_statuses?.map(status => (
                        <SelectItem key={status} value={status}>
                          {status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => handleFormChange('notes', e.target.value)}
                    placeholder="Additional notes..."
                    rows={3}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Tags</Label>
                  <div className="flex gap-2">
                    <Input
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      placeholder="Add a tag"
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    />
                    <Button type="button" variant="outline" onClick={addTag}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  {formData.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {formData.tags.map(tag => (
                        <Badge key={tag} variant="secondary" className="cursor-pointer" onClick={() => removeTag(tag)}>
                          {tag} <X className="w-3 h-3 ml-1" />
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>
              
              {/* Business Details Tab */}
              <TabsContent value="business" className="space-y-4 mt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Primary Specialty</Label>
                    <Select value={formData.specialty} onValueChange={(v) => handleFormChange('specialty', v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select specialty" />
                      </SelectTrigger>
                      <SelectContent>
                        {options.specialties.map(spec => (
                          <SelectItem key={spec} value={spec}>{spec}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>License Number</Label>
                    <Input
                      value={formData.license_number}
                      onChange={(e) => handleFormChange('license_number', e.target.value)}
                      placeholder="License #"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Tax ID / EIN</Label>
                    <Input
                      value={formData.tax_id}
                      onChange={(e) => handleFormChange('tax_id', e.target.value)}
                      placeholder="XX-XXXXXXX"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Payment Terms</Label>
                    <Select value={formData.payment_terms} onValueChange={(v) => handleFormChange('payment_terms', v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select terms" />
                      </SelectTrigger>
                      <SelectContent>
                        {options.payment_terms?.map(term => (
                          <SelectItem key={term} value={term}>{term}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Hourly Rate ($)</Label>
                    <Input
                      type="number"
                      value={formData.hourly_rate}
                      onChange={(e) => handleFormChange('hourly_rate', e.target.value)}
                      placeholder="0.00"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Day Rate ($)</Label>
                    <Input
                      type="number"
                      value={formData.day_rate}
                      onChange={(e) => handleFormChange('day_rate', e.target.value)}
                      placeholder="0.00"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Years in Business</Label>
                    <Input
                      type="number"
                      value={formData.years_in_business}
                      onChange={(e) => handleFormChange('years_in_business', e.target.value)}
                      placeholder="0"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Number of Employees</Label>
                    <Input
                      type="number"
                      value={formData.num_employees}
                      onChange={(e) => handleFormChange('num_employees', e.target.value)}
                      placeholder="0"
                    />
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="union"
                    checked={formData.union_affiliated}
                    onCheckedChange={(checked) => handleFormChange('union_affiliated', checked)}
                  />
                  <label htmlFor="union" className="text-sm">Union Affiliated</label>
                </div>
                
                <Separator />
                
                <div className="space-y-2">
                  <Label>Secondary Specialties</Label>
                  <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto p-2 border rounded-lg">
                    {options.specialties.filter(s => s !== formData.specialty).map(spec => (
                      <Badge
                        key={spec}
                        variant={formData.secondary_specialties.includes(spec) ? 'default' : 'outline'}
                        className="cursor-pointer"
                        onClick={() => toggleSecondarySpecialty(spec)}
                      >
                        {spec}
                      </Badge>
                    ))}
                  </div>
                </div>
              </TabsContent>
              
              {/* Insurance & Bonding Tab */}
              <TabsContent value="insurance" className="space-y-4 mt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Insurance Provider</Label>
                    <Input
                      value={formData.insurance_provider}
                      onChange={(e) => handleFormChange('insurance_provider', e.target.value)}
                      placeholder="Insurance company"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Policy Number</Label>
                    <Input
                      value={formData.insurance_policy_number}
                      onChange={(e) => handleFormChange('insurance_policy_number', e.target.value)}
                      placeholder="Policy #"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Coverage Amount ($)</Label>
                    <Input
                      type="number"
                      value={formData.insurance_coverage_amount}
                      onChange={(e) => handleFormChange('insurance_coverage_amount', e.target.value)}
                      placeholder="0"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Expiry Date</Label>
                    <Input
                      type="date"
                      value={formData.insurance_expiry}
                      onChange={(e) => handleFormChange('insurance_expiry', e.target.value)}
                    />
                  </div>
                </div>
                
                <Separator />
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Bonding Company</Label>
                    <Input
                      value={formData.bonding_company}
                      onChange={(e) => handleFormChange('bonding_company', e.target.value)}
                      placeholder="Bonding company"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Bonding Limit ($)</Label>
                    <Input
                      type="number"
                      value={formData.bonding_limit}
                      onChange={(e) => handleFormChange('bonding_limit', e.target.value)}
                      placeholder="0"
                    />
                  </div>
                </div>
              </TabsContent>
              
              {/* Certifications Tab */}
              <TabsContent value="certs" className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Certifications</Label>
                  <div className="flex flex-wrap gap-2 max-h-64 overflow-y-auto p-2 border rounded-lg">
                    {options.certifications.map(cert => (
                      <Badge
                        key={cert}
                        variant={formData.certifications.includes(cert) ? 'default' : 'outline'}
                        className="cursor-pointer"
                        onClick={() => toggleCertification(cert)}
                      >
                        {cert}
                      </Badge>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">Click to toggle certifications</p>
                </div>
              </TabsContent>
            </ScrollArea>
          </Tabs>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} data-testid="save-contractor-btn">
              <Save className="w-4 h-4 mr-2" />
              {editingContractor ? 'Update' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContractorDatabase;
